import { useState, useEffect, useCallback, useRef } from 'react';
import WorkflowMonitor from './WorkflowMonitor';
import { pipelineApi as defaultApi, describeError, nextPollDelay } from '../services/pipelineApi';
import { formatDay, describeAhead } from '../pipelineFormat';

// Sits under "Generate All" on the idea page: shows where THIS idea is in the
// workflow and points to the one place to review it. It never asks for decisions.
export default function IdeaPipelinePanel({ ideaId, refreshKey = 0, error = null, onOpenReview = () => {}, onRetry = () => {}, api = defaultApi, pollMs = 4000 }) {
  const [state, setState] = useState({ loading: true, pipeline: null, problem: null });
  const alive = useRef(true);
  // Set true on every mount: React's strict mode mounts, unmounts and mounts again in development.
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async () => {
    const res = await api.status(ideaId);
    if (!alive.current) return;
    if (res.ok && res.body && res.body.pipeline) setState({ loading: false, pipeline: res.body.pipeline, problem: null });
    else if (res.status === 404) setState({ loading: false, pipeline: null, problem: null });   // simply not started yet
    else setState((s) => ({ ...s, loading: false, problem: describeError(res) }));
  }, [api, ideaId]);

  useEffect(() => { setState({ loading: true, pipeline: null, problem: null }); load(); }, [load, refreshKey]);

  const status = state.pipeline && state.pipeline.status;
  useEffect(() => {
    if (!nextPollDelay(status)) return undefined;
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [status, load, pollMs]);

  const p = state.pipeline;
  if (!p && !error && !state.problem) return null;
  const sum = (p && p.scheduleSummary) || {};

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, margin: '10px 0' }}>
      {(error || state.problem) && <div role="alert" style={{ background: '#fef2f2', border: '1px solid #dc2626', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 13, color: '#7f1d1d' }}>{error || state.problem}</div>}
      {p && <WorkflowMonitor steps={p.monitor} compact />}

      {p && p.status === 'running' && <p style={{ fontSize: 12, color: '#64748b', margin: '10px 0 0' }}>The server is doing the work. You can close this tab and you will get a phone alert when it is ready.</p>}
      {p && p.status === 'scheduling' && <p style={{ fontSize: 12, color: '#64748b', margin: '10px 0 0' }}>Approved: scheduling every platform. Nothing for you to do.</p>}

      {p && p.status === 'ready' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, color: '#0f172a', marginBottom: 8 }}>Ready. Look it over and approve in one place.</div>
          <button type="button" onClick={onOpenReview} style={{ padding: '9px 16px', fontSize: 14, fontWeight: 800, borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer' }}>Open Review &amp; Publish {'→'}</button>
        </div>
      )}

      {p && p.status === 'completed' && (
        <div style={{ marginTop: 12 }}>
          <div role="status" style={{ fontSize: 13, color: '#14532d' }}>
            <strong>{sum.scheduled} post{sum.scheduled === 1 ? '' : 's'} scheduled</strong>{sum.lastAt ? `, through ${formatDay(sum.lastAt)} (${describeAhead(sum.lastAt)})` : ''}.
          </div>
          <button type="button" onClick={onOpenReview} style={{ marginTop: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>See what was scheduled {'→'}</button>
        </div>
      )}

      {p && p.status === 'failed' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, color: '#7f1d1d' }}>Something stopped this idea{p.error ? `: ${p.error}` : '.'}</div>
          <button type="button" onClick={onRetry} style={{ marginTop: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer' }}>Try again</button>
        </div>
      )}
    </div>
  );
}
